/* Scai.FM Radio, the public player on scai.fm: a stereo tower of three units (docs/player-design).
   Header strip with the soft keys; the receiver (display, meters, transport, presets, volume); and one lower unit
   that shows the DJ feed, the week's schedule or the public playlist. The stream keeps playing while you switch.
   Everyone hears the same stream; replaying a song from the playlist stops the stream for as long as it plays.
   Live: the tuned station's event stream (SSE) says what is on as soon as an item starts, and how many listen; a
   playing player sends a heartbeat (it counts as a listener). The DJ feed carries the shout box.
   URLs: /s/<station> (feed), /s/<station>/schedule, /s/<station>/playlist; / is the homepage (Home.tsx). */
import { For, Show, createEffect, createMemo, createResource, createSignal, onCleanup, onMount } from 'solid-js';
import { Annunciators, Faceplate, Glass, Key, Knob, Lamp, Readout, Spectrum, Sym, VuMeter, hzAt } from '../../hifi';
import { WEBAUDIO_METERS, createStreamPlayer } from '../../audio/player';
import { envelopeLevels, startMeters, webAudioLevels } from '../../audio/meters';
import { API, ApiError, PlaylistPage, RadioItem, RadioSchedule, RadioStation, api } from '../../api';

type Panel = 'feed' | 'schedule' | 'playlist';
const BANDS = 16;
const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
// How far behind the station a listener hears it, per source (ms), when the stream can't say: HLS carries a program
// date time (stamped on the stream host), so this is only the MP3 stream's guess, and HLS's until the first segment.
const LATENCY = { hlsjs: 20_000, native: 24_000, mp3: 3_000 } as const;
const LIVE = typeof EventSource !== 'undefined';
// One id per open player, random: what the listener count counts (never an address or a cookie).
const PLAYER_ID = Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, '0')).join('');

const upper = (s: string) => s.toUpperCase();
const fmt = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

/** "Preset 2 · Chill with Mr. Snuffles"; a station off the list has no preset, one without a host no "with". */
function stationLine(st: RadioStation): string {
  return `${st.preset ? `Preset ${st.preset} · ` : ''}${st.name}${st.dj ? ` with ${st.dj.name}` : ''}`;
}

function route(): { slug: string | null; panel: Panel } {
  const m = location.pathname.match(/^\/s\/([a-z][a-z0-9-]*)(?:\/(schedule|playlist))?\/?$/);
  return { slug: m ? m[1] : null, panel: (m?.[2] as Panel) ?? 'feed' };
}

export default function Radio() {
  const [stations, setStations] = createSignal<RadioStation[]>([]);
  const [serverSkew, setServerSkew] = createSignal(0); // server clock minus ours (ms)
  const [net, setNet] = createSignal(true);
  const [sel, setSel] = createSignal<string | null>(route().slug);
  const [panel, setPanel] = createSignal<Panel>(route().panel);
  const [clock, setClock] = createSignal(Date.now());
  const [volume, setVolume] = createSignal(80);
  const [muted, setMuted] = createSignal(false);
  const player = createStreamPlayer();
  let meters: ReturnType<typeof startMeters> | null = null;
  const needles: SVGLineElement[] = [];
  const vuSvgs: SVGSVGElement[] = [];
  let columns: HTMLDivElement[] = [];
  const timers: number[] = [];

  const station = createMemo(() => stations().find((s) => s.slug === sel()) ?? stations()[0] ?? null);
  const tuned = createMemo(() => station()?.slug ?? null); // changes only when another station is tuned
  const listening = () => ['playing', 'buffering', 'starting', 'blocked'].includes(player.state());

  async function load() {
    try {
      const r = await api<{ server_time: string; stations: RadioStation[] }>('/public/stations');
      const slug = sel();
      // a station that is not on the list (a Studio station) has a page of its own, with only itself on it
      const one = slug && !r.stations.some((s) => s.slug === slug)
        ? await api<{ server_time: string; station: RadioStation }>(`/public/stations/${slug}`).catch(() => null) : null;
      setStations(one ? [one.station] : r.stations);
      setServerSkew(Date.parse((one ?? r).server_time) - Date.now());
      setNet(true);
    } catch { setNet(false); }
  }

  // The tuned station's event stream: what is on (an item started, the station went on or off air) and the listener
  // count, pushed. The station list is still fetched now and then for the other presets.
  createEffect(() => {
    const slug = tuned();
    if (!slug || !LIVE) return;
    const es = new EventSource(`${API}/public/stations/${slug}/events`);
    es.addEventListener('station', (e) => {
      const d = JSON.parse((e as MessageEvent).data) as { server_time: string; station: RadioStation };
      setStations(stations().map((s) => (s.slug === d.station.slug ? { ...d.station, preset: s.preset } : s)));
      setServerSkew(Date.parse(d.server_time) - Date.now());
      setNet(true);
    });
    es.addEventListener('listeners', (e) => {
      const n = (JSON.parse((e as MessageEvent).data) as { count: number }).count;
      setStations(stations().map((s) => (s.slug === slug && s.listeners !== n ? { ...s, listeners: n } : s)));
    });
    onCleanup(() => es.close());
  });

  // While the stream plays, this player counts as a listener of its station.
  const playing = createMemo(() => player.state() === 'playing');
  createEffect(() => {
    const slug = tuned();
    if (!slug || !playing()) return;
    const beat = (playing: boolean) => fetch(`${API}/public/stations/${slug}/listening`, { method: 'POST',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ player: PLAYER_ID, playing }) }).catch(() => {});
    void beat(true);
    const t = window.setInterval(() => void beat(true), 30_000);
    onCleanup(() => { clearInterval(t); void beat(false); });
  });

  // What this listener hears now, on the server's clock: the stream reaches us late by the source's latency.
  const audibleAt = () => {
    const now = clock(); // the stream's clock is not a signal: the second's tick redraws what depends on it
    const ms = player.audibleMs();
    if (ms !== null) return ms;
    const src = player.source();
    const lag = player.state() === 'playing' && src ? LATENCY[src] : 0;
    return now + serverSkew() - lag;
  };
  const current = createMemo<RadioItem | null>(() => {
    const items = station()?.items ?? [];
    const t = audibleAt();
    let best: RadioItem | null = null;
    for (const i of items) if (Date.parse(i.started_at) <= t) best = i;
    return best;
  });
  const upNext = createMemo(() => {
    const items = station()?.items ?? [];
    const i = current() ? items.indexOf(current()!) : -1;
    return items.slice(i + 1).find((x) => x.track) ?? null;
  });

  function startAudio() {
    const st = station();
    if (!st) return;
    replay.stop();
    if (!meters) {
      try {
        meters = startMeters(WEBAUDIO_METERS ? webAudioLevels(player.audio, BANDS) : envelopeLevels({
          url: (key) => (key.startsWith('link:') ? `${API}/public/links/${key.slice(5)}/levels` : `${API}/public/levels/${key}`),
          audibleMs: () => (player.state() === 'playing' ? audibleAt() : null),
          bands: BANDS,
          items: () => (station()?.items ?? []).map((i) => ({ started_at: i.started_at, duration_ms: i.duration_ms,
            key: i.track?.id ?? (i.link_id ? `link:${i.link_id}` : null) })),
        }), () => ({ needles, vuSvgs, columns }), BANDS);
      } catch { /* no Web Audio: the meters rest */ }
    }
    void meters?.resume();
    player.setUrls(st.stream.hls, st.stream.mp3);
    player.setVolume(muted() ? 0 : volume() / 100);
    player.start();
  }

  function go(slug: string | null, p: Panel, push = true) {
    const was = listening();
    if (slug !== sel() && was) player.stop(); // switching presets stops the current stream before the next
    setSel(slug);
    setPanel(p);
    const path = !slug ? '/' : p === 'feed' ? `/s/${slug}` : `/s/${slug}/${p}`;
    if (push && location.pathname !== path) history.pushState(null, '', path);
    if (slug !== null && was && player.state() === 'idle') queueMicrotask(startAudio);
  }

  function applyVolume(v: number) {
    setVolume(v);
    if (!muted()) player.setVolume(v / 100);
  }
  function toggleMute() {
    setMuted(!muted());
    player.setVolume(muted() ? 0 : volume() / 100);
  }

  // A tap anywhere lets a suspended AudioContext (and so the meters' audio path) run.
  const unlock = () => { void meters?.resume(); if (player.state() === 'blocked') player.resume(); };
  onMount(async () => {
    for (const ev of ['pointerdown', 'keydown', 'touchend'] as const) document.addEventListener(ev, unlock, { capture: true });
    await load();
    timers.push(window.setInterval(load, LIVE ? 60_000 : 10_000));
    timers.push(window.setInterval(() => setClock(Date.now()), 1000));
    const pop = () => {
      if (location.pathname === '/') { location.reload(); return; } // back to the homepage
      const r = route(); go(r.slug, r.panel, false);
    };
    window.addEventListener('popstate', pop);
    onCleanup(() => window.removeEventListener('popstate', pop));
  });
  onCleanup(() => {
    for (const ev of ['pointerdown', 'keydown', 'touchend'] as const) document.removeEventListener(ev, unlock, { capture: true });
    timers.forEach(clearInterval);
    player.stop();
    meters?.stop();
  });
  // Stream down: retry silently every 10 s (the player's own watchdog moves between sources meanwhile).
  createEffect(() => { document.title = station() ? `${station()!.name} · scai.fm` : 'scai.fm'; });

  const replay = createReplay(() => { if (listening()) player.stop(); });

  const onAir = () => !!station()?.on_air;
  const offline = () => !net() || !onAir() || player.state() === 'offline';
  const words = createMemo(() => [
    offline() ? { text: 'OFFLINE', on: true, red: true } : { text: 'LIVE', on: true },
    { text: 'STEREO', on: player.state() === 'playing' },
    { text: 'AAC 128', on: player.state() === 'playing' && player.source() !== 'mp3' },
    { text: 'HLS', on: player.state() === 'playing' && player.source() !== 'mp3' },
    { text: 'REC', on: false },
    { text: 'MUTE', on: muted() },
    { text: 'BUFFER', on: player.state() === 'buffering' || player.state() === 'starting' },
  ]);
  const trackLine = () => {
    const st = station();
    if (!st) return net() ? 'TUNING' : 'NO SIGNAL';
    if (!st.on_air) return 'OFF AIR';
    if (player.state() === 'blocked') return 'PRESS ▶ TO LISTEN';
    const c = current();
    if (c?.kind === 'link') return upper(`${st.dj?.name ?? 'The DJ'} on the mic`);
    if (c?.kind === 'clip') return upper(st.name);  // the station's ident
    if (c?.track) return upper(`${c.track.title} — ${c.track.artist}`);
    return listening() ? '' : 'PRESS PLAY';
  };
  const elapsed = () => (current() ? audibleAt() - Date.parse(current()!.started_at) : 0);

  return (
    <div class="mx-auto flex min-h-screen w-full max-w-[1200px] flex-col gap-[14px] p-3 sm:p-6">
      <Faceplate as="header" class="flex flex-wrap items-center gap-x-6 gap-y-3 px-5 py-3 sm:px-6">
        <a href="/" class="flex items-center gap-3 no-underline">
          <img src="/mark.png" alt="" class="h-8 w-11 object-contain" />
          <span class="wordmark text-2xl font-semibold text-legend">scai.fm</span>
        </a>
        <Lamp tone={onAir() ? 'red' : 'off'} word="On air" />
        <nav class="-mb-1 flex w-full gap-3 overflow-x-auto pb-1 sm:ml-auto sm:w-auto" aria-label="Sections">
          <Key legend="Radio" latched={panel() === 'feed'} onClick={() => go(station()?.slug ?? null, 'feed')} />
          <Key legend="Schedule" latched={panel() === 'schedule'} onClick={() => go(station()?.slug ?? null, 'schedule')} />
          <Key legend="Playlist" latched={panel() === 'playlist'} onClick={() => go(station()?.slug ?? null, 'playlist')} />
          <Key legend="Zones" onClick={() => (location.href = 'https://zones.scai.fm')} />
        </nav>
      </Faceplate>

      <Faceplate as="main" class="grid grid-cols-12 gap-4 p-4 sm:p-6" label="Receiver">
        <Glass class="col-span-12 flex min-w-0 flex-col gap-3 px-5 py-5 md:col-span-8 sm:px-7">
          <Annunciators words={words()} class="flex-wrap !justify-start gap-x-5 sm:!justify-between" />
          <Readout size="m" tone="muted" text={station() ? upper(stationLine(station()!)) : 'SCAI.FM RADIO'} />
          <Readout size="xl" text={upper(station()?.programme.name ?? '—')} />
          <Readout size="l" tone={station()?.on_air === false || player.state() === 'blocked' ? 'muted' : 'lit'} text={trackLine()} live />
          <div class="mt-auto flex flex-wrap justify-between gap-x-4 gap-y-1 pt-2 text-[15px] tracking-[0.08em]">
            <span class="vfd whitespace-nowrap">
              <Show when={listening() && current()?.duration_ms} fallback={<span class="vfd-muted">--:-- / --:--</span>}>
                {fmt(elapsed())} / {fmt(current()!.duration_ms)}
              </Show>
            </span>
            <span class="vfd-muted min-w-0 truncate">{upNext()?.track ? `NEXT ${upper(upNext()!.track!.title)}` : ''}</span>
            <Show when={onAir() && station()!.listeners > 0}>
              <span class="vfd-muted whitespace-nowrap">{station()!.listeners} LISTENING</span>
            </Show>
            <span class={onAir() ? 'vfd-red' : 'vfd-off'}>ON AIR</span>
          </div>
        </Glass>

        <div class="col-span-12 grid grid-cols-1 gap-3 sm:grid-cols-2 md:col-span-4 md:grid-cols-1">
          <div class="grid grid-cols-2 gap-2.5">
            <Glass class="p-2"><VuMeter channel="L" needleRef={(el) => (needles[0] = el)} labelRef={(el) => (vuSvgs[0] = el)} /></Glass>
            <Glass class="p-2"><VuMeter channel="R" needleRef={(el) => (needles[1] = el)} labelRef={(el) => (vuSvgs[1] = el)} /></Glass>
          </div>
          <Glass class="min-h-[120px] px-3.5 pb-2 pt-3" role="img" aria-label="Spectrum analyser, sixteen bands">
            <Spectrum columns={BANDS} colRef={(els) => (columns = els)}
              scale={[{ label: '60', at: 0 }, { label: '250', at: hzAt(250) }, { label: '1k', at: hzAt(1000) }, { label: '4k', at: hzAt(4000) }, { label: '16k Hz', at: 1 }]} />
          </Glass>
        </div>

        <div class="col-span-12 flex flex-wrap items-end gap-x-8 gap-y-4 border-t border-hairline pt-4">
          <div class="flex flex-col gap-2">
            <span class="legend">Transport</span>
            <div class="flex gap-3">
              <Key symbol={<Sym name="play" />} aria-label="Listen" latched={listening()} disabled={!station()}
                onClick={() => (player.state() === 'blocked' ? player.resume() : listening() ? undefined : startAudio())} />
              <Key symbol={<Sym name="stop" />} aria-label="Stop" onClick={() => player.stop()} />
              <Key legend="Mute" latched={muted()} onClick={toggleMute} />
            </div>
          </div>
          <div class="order-first flex min-w-0 basis-full flex-col gap-2 sm:order-none sm:basis-auto sm:flex-1">
            <span class="legend">Presets</span>
            <div class="flex gap-3 overflow-x-auto pb-1 pt-0.5">
              <For each={stations()}>
                {(s) => (
                  <Key legend={s.preset ? `${s.preset} · ${s.name}` : s.name} lamp={s.slug === station()?.slug ? 'red' : 'off'} latched={s.slug === station()?.slug}
                    class="whitespace-nowrap" onClick={() => go(s.slug, panel())} />
                )}
              </For>
            </div>
          </div>
          <Knob label="Volume" value={volume()} max={100} onInput={applyVolume} />
        </div>
      </Faceplate>

      <Faceplate as="section" class="p-4 sm:p-6" label={{ feed: 'DJ feed', schedule: 'Schedule', playlist: 'Playlist' }[panel()]}>
        <Show when={station()}>
          {(st) => (
            <>
              <Show when={panel() === 'feed'}><Feed station={st()} audible={audibleAt} /></Show>
              <Show when={panel() === 'schedule'}><Schedule station={st()} at={clock() + serverSkew()} /></Show>
              <Show when={panel() === 'playlist'}><Playlist station={st()} replay={replay} /></Show>
            </>
          )}
        </Show>
        <p class="mt-6 flex flex-wrap gap-x-4 font-mono text-xs" style={{ color: 'var(--legend-muted)' }}>
          <span>Operated by ScaiLabs B.V.</span>
          <Show when={station()}><span>{station()!.stream.mp3.replace(/^https:\/\//, '')}</span></Show>
          <span>Everything on this station is made by AI.</span>
        </p>
      </Faceplate>
    </div>
  );
}

// --------------------------------------------------------------------------- DJ feed

/** What the DJ said: a line appears once this listener has heard it to the end (the stream reaches each listener
 *  with its own delay, so the server's "aired" is too early by that much). */
function Feed(props: { station: RadioStation; audible: () => number }) {
  const [data, { refetch }] = createResource(() => props.station.slug,
    (slug) => api<{ dj: string; lines: { text: string; aired_at: string; duration_ms: number }[] }>(`/public/stations/${slug}/feed`));
  const lines = createMemo(() => ({
    lines: (data()?.lines ?? []).filter((l) => Date.parse(l.aired_at) + l.duration_ms <= props.audible()),
  }));
  const t = window.setInterval(() => refetch(), 15_000);
  onCleanup(() => clearInterval(t));
  // Something started (a live event or a poll changed the station): a link that just ended is in the feed now. The
  // song after a link starts a second before the link ends, so ask again a moment later too.
  createEffect(() => {
    props.station.items;
    void refetch();
    const later = window.setTimeout(() => void refetch(), 3000);
    onCleanup(() => clearTimeout(later));
  });
  const time = (iso: string) => new Date(iso).toLocaleTimeString('en-GB', { timeZone: props.station.timezone, hour: '2-digit', minute: '2-digit' });
  return (
    <div class="flex flex-col gap-4">
      <span class="legend">{props.station.dj ? `DJ feed · ${props.station.dj.name}` : 'Feed'}</span>
      <Glass class="min-h-[140px] px-5 py-4" aria-label="What the DJ said, newest first">
        <Show when={props.station.dj?.talks} fallback={<p class="font-mono text-[15px] vfd-muted">{props.station.dj ? `${props.station.dj.name.toUpperCase()} DOES NOT TALK. THE MUSIC PLAYS.` : 'NO HOST ON THIS STATION. THE MUSIC PLAYS.'}</p>}>
          <Show when={lines().lines.length} fallback={<p class="font-mono text-[15px] vfd-muted">{props.station.on_air ? 'NOTHING SAID IN THE LAST HALF HOUR' : 'OFF AIR'}</p>}>
            <ol class="flex flex-col gap-2.5 font-mono text-[15px] leading-snug">
              <For each={lines().lines}>
                {(l, i) => (
                  <li class={i() === lines().lines.length - 1 && i() > 0 ? 'vfd-muted' : 'vfd'}>
                    <span class="mr-3 whitespace-nowrap">{time(l.aired_at)}</span>{l.text.replace(/\[[a-z-]+\]\s*/g, '')}
                  </li>
                )}
              </For>
            </ol>
          </Show>
        </Show>
      </Glass>
      <Show when={props.station.shouts}><ShoutBox station={props.station} /></Show>
      <div class="grid gap-3 text-[15px] sm:grid-cols-2" style={{ color: 'var(--legend-muted)' }}>
        <p>{props.station.description}</p>
        <Show when={props.station.dj}>{(dj) => <p><span class="legend mr-2" style={{ color: 'var(--legend)' }}>{dj().name}</span>{dj().bio}</p>}</Show>
      </div>
    </div>
  );
}

/** A line to the DJ. No account: an optional name (never said on air). The filter decides what the DJ reads; the
 *  answer, if any, comes on air like everything else. Lamp: green when sent, amber when refused or too many. */
function ShoutBox(props: { station: RadioStation }) {
  const dj = () => props.station.dj?.name ?? 'the DJ'; // shouts are open only while a DJ talks
  const [text, setText] = createSignal('');
  const [name, setName] = createSignal('');
  const [lamp, setLamp] = createSignal<'off' | 'green' | 'warn'>('off');
  const [note, setNote] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  let lampTimer: number | undefined;
  const flash = (tone: 'green' | 'warn', ms: number) => {
    setLamp(tone);
    clearTimeout(lampTimer);
    lampTimer = window.setTimeout(() => setLamp('off'), ms);
  };
  onCleanup(() => clearTimeout(lampTimer));
  async function send(e?: Event) {
    e?.preventDefault();
    const t = text().trim();
    if (!t || busy()) return;
    setBusy(true);
    try {
      await api(`/public/stations/${props.station.slug}/shouts`, { method: 'POST',
        body: JSON.stringify({ text: t, name: name().trim() || null }) });
      setText('');
      setNote('Sent. If it gets past the filter, listen out: the DJ may pick it up after a song or two.');
      flash('green', 600);
    } catch (err) {
      setNote(err instanceof ApiError && [409, 422, 429].includes(err.status) ? `${err.message[0].toUpperCase()}${err.message.slice(1)}.`
        : 'That did not go through; try again in a moment.');
      flash('warn', 4000);
    } finally { setBusy(false); }
  }
  return (
    <form class="flex flex-col gap-2" onSubmit={send}>
      <span class="legend">Shout to {dj()}</span>
      <div class="flex flex-wrap items-end gap-3">
        <input value={text()} maxLength={200} placeholder="Ask for a mood, a topic, say hello…" aria-label={`Your shout to ${dj()}`}
          onInput={(e) => setText(e.currentTarget.value)}
          class="h-[46px] min-w-0 flex-[3_1_16rem] rounded-[3px] border border-edge bg-glass px-4 font-mono text-[15px] text-legend" />
        <input value={name()} maxLength={40} placeholder="Name (optional)" aria-label="Your name (optional, never said on air)"
          onInput={(e) => setName(e.currentTarget.value)}
          class="h-[46px] min-w-0 flex-[1_1_9rem] rounded-[3px] border border-edge bg-glass px-4 font-mono text-[15px] text-legend" />
        <Key legend="Send" symbol={<Sym name="play" />} primary lamp={lamp()} disabled={busy() || !text().trim()} onClick={() => void send()} />
      </div>
      <p class="text-sm" role="status" style={{ color: lamp() === 'warn' ? 'var(--lamp-warn)' : 'var(--legend-muted)' }}>
        {note() || `A filter reads every shout first; ${dj()} sees what it lets through. Everyone hears the same stream, and names are never said on air.`}
      </p>
    </form>
  );
}

// --------------------------------------------------------------------------- schedule

function Schedule(props: { station: RadioStation; at: number }) {
  const [sc] = createResource(() => props.station.slug, (slug) => api<RadioSchedule>(`/public/stations/${slug}/schedule`));
  const name = (slug: string) => sc()?.programmes.find((p) => p.slug === slug)?.name ?? slug;
  const mins = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  /** One day as back-to-back stretches: the day's slots, a slot spilling over from the night before, the base
   *  format in the gaps. */
  const day = (d: number) => {
    const s = sc();
    if (!s) return [];
    const parts: { from: number; slug: string }[] = [];
    for (const x of s.slots) {
      const a = mins(x.start), b = mins(x.end), over = b <= a;
      if (x.weekday === d) parts.push({ from: a, slug: x.programme });
      if (over && (x.weekday % 7) + 1 === d && b > 0) parts.push({ from: 0, slug: x.programme }, { from: b, slug: '' });
      else if (x.weekday === d && !over) parts.push({ from: b, slug: '' });
    }
    parts.sort((p, q) => p.from - q.from || (p.slug ? 1 : -1));
    const out: { from: number; slug: string }[] = [];
    for (const p of parts) {
      const last = out[out.length - 1];
      if (last && last.from === p.from) { if (p.slug) last.slug = p.slug; continue; }
      out.push({ ...p });
    }
    if (!out.length || out[0].from > 0) out.unshift({ from: 0, slug: '' });
    return out.filter((p, i) => i === 0 || p.slug !== out[i - 1].slug).filter((p) => p.from < 1440);
  };
  const local = () => {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: props.station.timezone, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
      .formatToParts(new Date(props.at));
    const wd = DAYS.indexOf(parts.find((p) => p.type === 'weekday')!.value.toUpperCase()) + 1;
    return { wd, m: Number(parts.find((p) => p.type === 'hour')!.value) * 60 + Number(parts.find((p) => p.type === 'minute')!.value) };
  };
  const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  return (
    <div class="flex flex-col gap-4">
      <span class="legend">Schedule · {props.station.name} · times in {props.station.timezone.replace('_', ' ')}</span>
      <Glass class="overflow-x-auto px-5 py-4" aria-label="This week's programmes">
        <Show when={sc()} fallback={<p class="vfd-muted text-[15px]">TUNING</p>}>
          <table class="w-full text-[15px] tracking-[0.06em]">
            <tbody>
              <For each={DAYS}>
                {(d, i) => (
                  <tr class="align-top">
                    <th scope="row" class={`w-14 py-1 pr-4 text-left font-normal ${local().wd === i() + 1 ? 'vfd' : 'vfd-muted'}`}>{d}</th>
                    <td class="py-1">
                      <div class="flex flex-wrap gap-x-5 gap-y-1">
                        <For each={day(i() + 1)}>
                          {(p, k) => {
                            const list = day(i() + 1);
                            const end = k() + 1 < list.length ? list[k() + 1].from : 1440;
                            const now = local().wd === i() + 1 && local().m >= p.from && local().m < end;
                            return <span class={now ? 'vfd' : 'vfd-muted'}>{hhmm(p.from)} {upper(p.slug ? name(p.slug) : sc()!.base)}</span>;
                          }}
                        </For>
                      </div>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </Glass>
      <dl class="grid gap-x-6 gap-y-2 text-[15px] sm:grid-cols-2">
        <For each={sc()?.programmes ?? []}>
          {(p) => <div><dt class="legend" style={{ color: 'var(--legend)' }}>{p.name}{p.slug === 'default' ? ' · the rest of the week' : ''}</dt>
            <dd style={{ color: 'var(--legend-muted)' }}>{p.description}</dd></div>}
        </For>
      </dl>
    </div>
  );
}

// --------------------------------------------------------------------------- playlist and replay

function createReplay(onStart: () => void) {
  const audio = new Audio();
  audio.preload = 'none';
  const [playing, setPlaying] = createSignal<string | null>(null);
  const [left, setLeft] = createSignal(0);
  const [error, setError] = createSignal<string | null>(null);
  audio.addEventListener('timeupdate', () => setLeft((audio.duration || 0) - audio.currentTime));
  audio.addEventListener('ended', () => setPlaying(null));
  return {
    playing, left, error,
    async play(trackId: string, row = trackId) {
      setError(null);
      try {
        const r = await api<{ url: string }>(`/public/tracks/${trackId}/listen`);
        onStart();
        audio.src = r.url;
        setPlaying(row);
        await audio.play();
      } catch (e) {
        setPlaying(null);
        setError(e instanceof ApiError && e.status === 429 ? 'Lots of replays from here; try again in a while.' : 'That song would not play.');
      }
    },
    stop() { audio.pause(); audio.removeAttribute('src'); audio.load(); setPlaying(null); },
  };
}

function Playlist(props: { station: RadioStation; replay: ReturnType<typeof createReplay> }) {
  const [pages, setPages] = createSignal<PlaylistPage[]>([]);
  const [busy, setBusy] = createSignal(false);
  const more = async (before?: string | null) => {
    setBusy(true);
    try {
      const q = before ? `?before=${encodeURIComponent(before)}` : '';
      const p = await api<PlaylistPage>(`/public/stations/${props.station.slug}/playlist${q}`);
      setPages(before ? [...pages(), p] : [p]);
    } finally { setBusy(false); }
  };
  createEffect(() => { props.station.slug; void more(); });
  const items = () => pages().flatMap((p) => p.items);
  const last = () => pages()[pages().length - 1]?.before ?? null;
  const when = (iso: string) => {
    const d = new Date(iso);
    const opts = { timeZone: props.station.timezone, hour: '2-digit', minute: '2-digit' } as const;
    const today = d.toLocaleDateString('en-GB', { timeZone: props.station.timezone }) === new Date().toLocaleDateString('en-GB', { timeZone: props.station.timezone });
    return today ? d.toLocaleTimeString('en-GB', opts) : d.toLocaleString('en-GB', { ...opts, weekday: 'short' });
  };
  return (
    <div class="flex flex-col gap-4">
      <span class="legend">Playlist · {props.station.name} · every song that went on air, to play again in full</span>
      <Show when={props.replay.error()}><p class="text-[15px]" style={{ color: 'var(--lamp-warn)' }}>{props.replay.error()}</p></Show>
      <Show when={items().length} fallback={<p class="text-[15px]" style={{ color: 'var(--legend-muted)' }}>{busy() ? 'Loading…' : 'Nothing has aired on this station yet.'}</p>}>
        <ol class="flex flex-col divide-y divide-hairline">
          <For each={items()}>
            {(it) => {
              const row = `${it.played_at}|${it.track.id}`;
              const mine = () => props.replay.playing() === row;
              return (
                <li class="flex items-center gap-4 py-2">
                  <span class="w-20 shrink-0 font-mono text-xs" style={{ color: 'var(--legend-muted)' }}>{when(it.played_at)}</span>
                  <span class="min-w-0 flex-1 truncate text-[15px]"><b class="font-semibold">{it.track.title}</b>
                    <span style={{ color: 'var(--legend-muted)' }}> — {it.track.artist}</span></span>
                  <span class="hidden font-mono text-xs sm:inline" style={{ color: 'var(--legend-muted)' }}>{mine() ? `-${fmt(props.replay.left() * 1000)}` : fmt(it.track.duration_ms)}</span>
                  <Key symbol={<Sym name={mine() ? 'stop' : 'play'} />} aria-label={`${mine() ? 'Stop' : 'Play'} ${it.track.title}`} latched={mine()}
                    onClick={() => (mine() ? props.replay.stop() : props.replay.play(it.track.id, row))} />
                </li>
              );
            }}
          </For>
        </ol>
        <Show when={last()}><div><Key legend={busy() ? 'Loading' : 'More'} disabled={busy()} onClick={() => more(last())} /></div></Show>
      </Show>
    </div>
  );
}
